import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import { Tooltip } from "antd";

export interface WorkflowNodeData {
  label: string;
  name: string;
  state: string;
  nodeType: "start" | "end" | "auto" | "active" | "completed" | "pending";
  isStart: boolean;
  isEnd: boolean;
  isAuto: boolean;
  icon?: string; // Icon path from Aras (e.g., "../images/Delete.svg", "../images/Checkmark.svg")
}

interface WorkflowNodeProps {
  data: WorkflowNodeData;
}

/**
 * Determine if Aras icon indicates a checkmark/success icon
 */
const isCheckmarkIcon = (arasIcon?: string): boolean => {
  if (!arasIcon) return false;
  const iconLower = arasIcon.toLowerCase();
  // Common Aras checkmark/success icon patterns
  return (
    iconLower.includes("checkmark") ||
    iconLower.includes("check") ||
    iconLower.includes("success") ||
    iconLower.includes("complete") ||
    iconLower.includes("done") ||
    iconLower.includes("ok") ||
    iconLower.includes("accept")
  );
};

/**
 * Get node configuration based on type and state
 */
const getNodeConfig = (
  nodeType: string,
  state: string,
  isStart: boolean,
  isEnd: boolean,
  isAuto: boolean,
  arasIcon?: string,
) => {
  // Start node - blue circle with play icon
  if (isStart) {
    return {
      fillColor: "#e6f4ff",
      strokeColor: "#1890ff",
      iconColor: "#1890ff",
      textColor: "#1890ff",
      icon: "play",
      shape: "circle",
    };
  }

  // End node - icon based on Aras icon property (checkmark = green, delete/X = red)
  if (isEnd) {
    // Use Aras icon to determine if it's a success/checkmark end or delete/X end
    if (isCheckmarkIcon(arasIcon)) {
      return {
        fillColor: "#f6ffed",
        strokeColor: "#52c41a",
        iconColor: "#52c41a",
        textColor: "#52c41a",
        icon: "check",
        shape: "circle",
      };
    }
    // Default End node is red with X (Delete icon in Aras)
    return {
      fillColor: "#fff2f0",
      strokeColor: "#ff4d4f",
      iconColor: "#ff4d4f",
      textColor: "#ff4d4f",
      icon: "close",
      shape: "circle",
    };
  }

  // Auto activity - diamond shape (cyan)
  if (isAuto) {
    return {
      fillColor: state === "Pending" ? "#e6fffb" : "#e6fffb",
      strokeColor: "#13c2c2",
      iconColor: "#13c2c2",
      textColor: "#13c2c2",
      icon: "sync",
      shape: "diamond",
      dashed: state === "Pending",
    };
  }

  // Active node - orange with glow
  if (nodeType === "active" || state === "Active") {
    return {
      fillColor: "#fff7e6",
      strokeColor: "#fa8c16",
      iconColor: "#fa8c16",
      textColor: "#fa8c16",
      icon: "active",
      shape: "circle",
      glow: true,
    };
  }

  // Completed node - green
  if (nodeType === "completed" || state === "Closed") {
    return {
      fillColor: "#f6ffed",
      strokeColor: "#52c41a",
      iconColor: "#52c41a",
      textColor: "#52c41a",
      icon: "check",
      shape: "circle",
    };
  }

  // Pending node - gray (default)
  return {
    fillColor: "#fafafa",
    strokeColor: "#d9d9d9",
    iconColor: "#8c8c8c",
    textColor: "#8c8c8c",
    icon: "clock",
    shape: "circle",
  };
};

/**
 * Render icon inside the shape based on node type
 */
const renderIcon = (icon: string, color: string) => {
  switch (icon) {
    case "play":
      // Triangle pointing right (Start)
      return <polygon points="16,12 16,28 28,20" fill={color} stroke="none" />;
    case "check":
      // Checkmark (Completed/End completed)
      return (
        <path
          d="M12 20l5 5 11-11"
          stroke={color}
          strokeWidth="3"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      );
    case "close":
      // X mark (End not completed)
      return (
        <>
          <line
            x1="13"
            y1="13"
            x2="27"
            y2="27"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <line
            x1="27"
            y1="13"
            x2="13"
            y2="27"
            stroke={color}
            strokeWidth="3"
            strokeLinecap="round"
          />
        </>
      );
    case "sync":
      // Circular arrows (Auto)
      return (
        <g transform="translate(20, 20)">
          <path
            d="M-6,-8 A10,10 0 1,1 -8,6"
            stroke={color}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          <polygon points="-10,4 -6,10 -2,4" fill={color} />
          <path
            d="M6,8 A10,10 0 1,1 8,-6"
            stroke={color}
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
          />
          <polygon points="10,-4 6,-10 2,-4" fill={color} />
        </g>
      );
    case "active":
      // Play/Active indicator (double arrow)
      return (
        <g>
          <polygon points="14,14 14,26 22,20" fill={color} />
          <polygon points="20,14 20,26 28,20" fill={color} />
        </g>
      );
    case "clock":
    default:
      // Clock (Pending)
      return (
        <g>
          <circle
            cx="20"
            cy="20"
            r="8"
            fill="none"
            stroke={color}
            strokeWidth="2"
          />
          <line
            x1="20"
            y1="20"
            x2="20"
            y2="14"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
          <line
            x1="20"
            y1="20"
            x2="24"
            y2="22"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
          />
        </g>
      );
  }
};

/**
 * Render shape - circle or diamond
 */
const renderShape = (
  shape: string,
  fillColor: string,
  strokeColor: string,
  dashed?: boolean,
) => {
  if (shape === "diamond") {
    // Diamond/rhombus shape for Auto activities (like Aras Innovator)
    return (
      <polygon
        points="20,2 38,20 20,38 2,20"
        fill={fillColor}
        stroke={strokeColor}
        strokeWidth="2"
        strokeDasharray={dashed ? "4,2" : undefined}
      />
    );
  }
  // Circle shape for other activities
  return (
    <circle
      cx="20"
      cy="20"
      r="18"
      fill={fillColor}
      stroke={strokeColor}
      strokeWidth="2"
      strokeDasharray={dashed ? "4,2" : undefined}
    />
  );
};

/**
 * Universal node component for workflow activities
 * Uses circle shape for most activities, diamond for Auto activities (matches Aras Innovator)
 */
const ShapeNode: React.FC<{
  label: string;
  config: ReturnType<typeof getNodeConfig>;
  isStart: boolean;
  isEnd: boolean;
}> = ({ label, config, isStart, isEnd }) => {
  const {
    fillColor,
    strokeColor,
    iconColor,
    textColor,
    icon,
    shape,
    dashed,
    glow,
  } = config as ReturnType<typeof getNodeConfig> & {
    dashed?: boolean;
    glow?: boolean;
  };

  // Only show icons for Start and End nodes
  const showIcon = isStart || isEnd;

  return (
    <div
      style={{
        position: "relative",
        width: 70,
        height: 90,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <svg
        width="50"
        height="50"
        viewBox="0 0 40 40"
        style={{
          filter: glow ? `drop-shadow(0 0 6px ${strokeColor})` : undefined,
        }}
      >
        {renderShape(shape || "circle", fillColor, strokeColor, dashed)}
        {showIcon && renderIcon(icon || "clock", iconColor)}
      </svg>
      <span
        style={{
          fontSize: 11,
          fontWeight: 500,
          color: textColor,
          marginTop: 4,
          textAlign: "center",
          maxWidth: 70,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {/* Target handle - not for start nodes */}
      {!isStart && (
        <Handle
          type="target"
          position={Position.Left}
          style={{
            width: 8,
            height: 8,
            background: strokeColor,
            border: "none",
            left: 8,
            top: 25,
          }}
        />
      )}
      {/* Source handle - not for end nodes */}
      {!isEnd && (
        <Handle
          type="source"
          position={Position.Right}
          style={{
            width: 8,
            height: 8,
            background: strokeColor,
            border: "none",
            right: 8,
            top: 25,
          }}
        />
      )}
    </div>
  );
};

/**
 * Custom node component for workflow visualization (matches Aras Innovator style):
 * - Start: Blue circle with play icon
 * - Auto: Cyan diamond with sync icon (dashed border when pending)
 * - Active: Orange circle with double-arrow icon (glowing)
 * - Completed: Green circle with checkmark
 * - Pending: Gray circle with clock icon
 * - End: Red circle with X (or green with checkmark when completed)
 */
export const WorkflowNode = memo(({ data }: WorkflowNodeProps) => {
  const config = getNodeConfig(
    data.nodeType,
    data.state,
    data.isStart,
    data.isEnd,
    data.isAuto,
    data.icon, // Pass Aras icon to determine End node appearance
  );

  return (
    <Tooltip
      title={
        <div>
          <div>
            <strong>{data.name}</strong>
          </div>
          <div>State: {data.state}</div>
          {data.isAuto && <div>Auto-execute</div>}
        </div>
      }
    >
      <ShapeNode
        label={data.label || data.name}
        config={config}
        isStart={data.isStart}
        isEnd={data.isEnd}
      />
    </Tooltip>
  );
});

WorkflowNode.displayName = "WorkflowNode";

export default WorkflowNode;
